import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import Utility from '../../../../../util/utilityfunctions';
import { currentResource, isNullOrUndefined, labels } from '../../../../../util/utils';
import { Entity, extendedPagingOptions, pagingOptions } from '../../../../../util/uiconstants';
import {useIsAresView} from '../../../../../util/Ares/customHooks';
import {deleteMultiEntity, getALLMultiEntity} from '../../../../../actions/MultiEntities/stEntitiesactions';
import {getAllStEntities, getStEntitiesPagingOptions} from '../../../../../selectors/Ares/selectors';
import GrayFade from '../../../../Common/GrayFade/GrayFade';
import LoadingIndicator from '../../../../Common/LoadingIndicator/LoadingIndicator';
import ProgressBar from '../../../../Common/BaseComponentWrappers/MotifProgressBar';
import ConfirmationModal from '../../../Modal/Modal';
import CreateEditEntity from '../../../../Common/MultiEntity/CreateEditEntity/CreateEditEntity';
import CreateEditMestModalWrapper from '../../../../Common/EntityCrud/CreateEditMestModalWrapper';
import ManageEntities from '../../../../Common/MultiEntity/ManageEntities/ManageEntities';
import StringColumn from './MultiEntityListTable/StringColumn';
import IconColumn from './MultiEntityListTable/IconColumn';
import MultiEntityListSearch from './MultiEntitySearch';
import MultiEntityListNoEntities from './MultiEntityListNoEntities';
import MultiEntityListTable from './MultiEntityListTable';

export default function MultiEntityList(props) {
	const {engagementId, getBodiesBySectionId} = props;
	const stEntities = useSelector((state) => getAllStEntities(state));
	const stPagingOptions = useSelector((state) => getStEntitiesPagingOptions(state));

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingEditDelete, setIsLoadingEditDelete] = useState(false);

	const [searchText, setSearchText] = useState(null);

	// Edit and delete modals
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showModal, setShowModal] = useState(false);

	const [selectedStEntity, setSelectedStEntity] = useState();

	const deleteDescription = currentResource.EntitiesLabels[Entity.STEntity]?.deleteConfirmLabel;
	const deleteHeaderTitle = currentResource.EntitiesLabels[Entity.STEntity]?.deleteEntity;

	const dispatch = useDispatch();

	const isAresView = useIsAresView();

	// Default page size 50, no everywhere else is 20, why 50 here?
	const [pageSize, setPageSize] = useState(pagingOptions.options[0]);
	const [pageNumber, setPageNumber] = useState(1);

	const getMultiEntities = useCallback(
		(_searchText, _pageNumber, _pageSize) => {
			setIsLoading(true);

			const payloadSearchText = isNullOrUndefined(_searchText) ? searchText : _searchText;
			const payloadPageNumber = Number(_pageNumber) || pageNumber;
			const payloadPageSize = _pageSize || pageSize;

			const payload = {
				searchString: payloadSearchText,
				page: Utility.isValidNumber(payloadPageNumber) ? payloadPageNumber : 1,
				pageSize: payloadPageSize
			};

			return dispatch(getALLMultiEntity(engagementId, payload)).finally(() => setIsLoading(false));
		},
		[engagementId, searchText, pageNumber, pageSize]
	);

	const onManageEntitiesClose = useCallback(() => getBodiesBySectionId(), [getBodiesBySectionId]);

	const columnDefs = useMemo(
		() => [
			{
				field: 'stEntityShortName',
				headerName: labels.multiEntityCode,
				cellRenderer: 'stringColumn',
				maxWidth: '350'
			},
			{
				field: 'stEntityName',
				headerName: labels.multiEntityName,
				cellRenderer: 'stringColumn',
				// cellRendererParams: {width: 'var(--px-500)'},
				minWidth: '350'
			},
			{
				field: 'actions',
				headerName: labels.multiEntityActions,
				cellRenderer: 'iconColumn',
				cellRendererParams: {
					//getDataRow: getMultiEntities,
					isLoading: isLoadingEditDelete,
					setSelectedStEntity: setSelectedStEntity,
					setShowModal: setShowModal,
					setShowDeleteModal: setShowDeleteModal,
					engagementId: engagementId
				},
				maxWidth: '120'
			}
		],
		[engagementId, getMultiEntities]
	);

	const onDeleteEntityClick = useCallback(() => {
		setIsLoadingEditDelete(true);
		return dispatch(deleteMultiEntity(engagementId, selectedStEntity.id)).finally(() => {
			setIsLoadingEditDelete(false);
			setShowDeleteModal(false);
			setSelectedStEntity();
			let _pageNumber = stEntities.length === 1 && pageNumber > 1 ? pageNumber - 1 : pageNumber;
			getMultiEntities('', _pageNumber, undefined);
		});
	}, [engagementId, selectedStEntity, getMultiEntities]);

	const onDeleteCancelClick = () => {
		setShowDeleteModal(false);
	};

	const handleOnClose = useCallback(() => {
		setShowModal(false);
		return getMultiEntities();
	}, [getMultiEntities]);

	const handleOnSearch = useCallback(
		(text) => {
			setSearchText(text);
			setPageNumber(1);

			return getMultiEntities(text, 1, undefined);
		},
		[getMultiEntities]
	);

	const handleOnClear = useCallback(() => {
		setSearchText('');
		return getMultiEntities('', undefined, undefined);
	}, [getMultiEntities]);

	const handleOnPageSizeChanged = useCallback(
		(_pageSize) => {
			setPageSize(_pageSize);
			setPageNumber(1);
			return getMultiEntities(undefined, 1, _pageSize);
		},
		[getMultiEntities]
	);

	const handleOnPageChanged = useCallback(
		(_pageNumber, evt) => {
			let promise = Promise.resolve();

			setPageNumber(Number(_pageNumber));
			promise = getMultiEntities(undefined, _pageNumber, undefined); //onDropdownChange

			return promise;
		},
		[getMultiEntities, pageNumber]
	);

	const stEntityCount = stEntities?.length || 0;
	let showNoEntitiesText;
	let sortedEntities = [];

	if (stEntityCount === 0 && !isLoading && isNullOrUndefined(searchText)) {
		showNoEntitiesText = true;
	} else if (stEntityCount > 0) {
		sortedEntities = stEntities.sort(Utility.sortByMultipleKey('stEntityName'));
		showNoEntitiesText = false;
	}

	useEffect(() => {
		getMultiEntities();
	}, []);

	const multiEntityTableRender = (
		<MultiEntityListTable
			columnDefs={columnDefs}
			rowData={sortedEntities}
			rowHeight={60}
			frameworkComponents={{
				stringColumn: StringColumn,
				iconColumn: IconColumn
			}}
			optionsPerPage={extendedPagingOptions.options}
			page={stPagingOptions?.page || 1}
			pageSize={pageSize}
			pageCount={stPagingOptions?.pages || 1}
			onPageSizeChanged={handleOnPageSizeChanged}
			onPageChanged={handleOnPageChanged}
			paginationSelectLabel={labels.pagingShowtext}
		/>
	);

	return (
		<StyledMultiEntityList className="StyledMultiEntityList">
			<GrayFade isGrayFade={isLoading} className="multy-entity-gray-fade">
				<>
					{showNoEntitiesText ? (
						<MultiEntityListNoEntities setShowModal={setShowModal} />
					) : (
						<>
							<section className="top-options-wrapper">
								<MultiEntityListSearch initialText={searchText} onSearch={handleOnSearch} onClear={handleOnClear} />
								<section className="entity-options-container">
									<CreateEditEntity isCreate className="create-multi-entity" onClose={handleOnClose} />
									<span className="LinkSeparator">|</span>
									<ManageEntities onClose={onManageEntitiesClose} className="manage-entities" />
								</section>
							</section>
							{isLoading ? (
								<LoadingIndicator show={isLoading} view="inlineView" />
							) : (
								<>
									{multiEntityTableRender}
									{showDeleteModal && selectedStEntity && (
										<ConfirmationModal
											show={showDeleteModal}
											showButtons
											title={deleteHeaderTitle}
											confirmBtnTitle={labels.delete}
											cancelBtnTitle={labels.cancelLabel}
											onOkClick={onDeleteEntityClick}
											onHide={onDeleteCancelClick}
											closeTitle={labels.closeLabel}
											modalContainerClass="deleteEntityModalWrapper"
											modalsize="x-small"
											isAresView={isAresView}
											isOkButtonDisabled={isLoadingEditDelete}
											isCancelButtonDisabled={isLoadingEditDelete}
										>
											<StyledDeleteModalBody className="StyledIconColumnModal">
												{isLoadingEditDelete && (
													<section className="MotifProgressBarWrapper">
														<ProgressBar className="IconColumnLoader" isIndeterminate circle hideLabel />
													</section>
												)}
												<section className="modalBodyWrapper customScrollbar">
													<section className="modalBodyDesc">{deleteDescription}</section>
													<section className="modalBodyText">{selectedStEntity.stEntityName}</section>
												</section>
											</StyledDeleteModalBody>
										</ConfirmationModal>
									)}
								</>
							)}
						</>
					)}
					{showModal && (
						<CreateEditMestModalWrapper
							isCreate={!selectedStEntity?.id}
							isEdit={!!selectedStEntity?.id}
							showModal={showModal}
							setShowModal={setShowModal}
							entityId={selectedStEntity?.id}
							onClose={handleOnClose}
						/>
					)}
				</>
			</GrayFade>
		</StyledMultiEntityList>
	);
}

const StyledMultiEntityList = styled.section`
	.multy-entity-gray-fade {
		&.grayFade {
			opacity: 0.6;
		}

		.top-options-wrapper {
			display: flex;
			justify-content: space-between;
			align-items: center;
			.entity-options-container {
				display: flex;
				flex-direction: row;
				align-items: center;
				margin-bottom: var(--px-32);
				.LinkSeparator {
					display: flex;
					align-items: center;
					margin: 0 var(--px-10);
					color: var(--blue-600);
					font-weight: bold;
					line-height: var(--px-22);
				}
				.manage-entities {
					margin-bottom: var(--px-0);
					font-size: var(--px-14);
				}
				.create-multi-entity {
					font-size: var(--px-14);
				}
			}
		}
	}
`;

const StyledDeleteModalBody = styled.section`
	.MotifProgressBarWrapper {
		width: 100%;
		height: calc(100% - var(--px-70));
		display: flex;
		align-items: center;
		justify-content: center;
		position: absolute;
		z-index: 9;
		background: var(--neutrals-00white) CC;
		top: var(--px-70);
		left: 0;
	}
	.modalBodyWrapper {
		min-height: 100%;
		.modalBodyDesc {
			font-size: var(--px-14) !important;
			font-weight: 300 !important;
			line-height: var(--px-24) !important;
			color: var(--neutrals-900) !important;
			padding-bottom: var(--px-24);
			border-bottom: var(--px-1) solid var(--neutrals-200);
		}
		.modalBodyText {
			padding: var(--px-24) 0 0 0;
			font-size: var(--px-14) !important;
			font-weight: 400 !important;
			line-height: var(--px-24) !important;
			color: var(--neutrals-700) !important;
			margin: 0 !important;
			border: 0 !important;
		}
	}
`;
